/**
 * Z-Index Tokens for Chakra Theme
 *
 * ## Usage
 *
 * ### Chakra Components (preferred)
 * ```tsx
 * <Box zIndex="sticky" />
 * <Flex zIndex="modal" />
 * ```
 *
 * ### Styled-components (requires raw constants)
 * ```tsx
 * import { Z_INDEX } from "@shared/theme/props/zIndexes";
 *
 * const Container = styled.div`
 *   z-index: ${Z_INDEX.sticky};
 * `;
 * ```
 *
 * ## ⚠️ Technical Debt: styled-components
 *
 * Styled-components using `Z_INDEX` constants should be refactored to use
 * proper Chakra components with semantic props. The `Z_INDEX` export exists
 * only as a bridge for legacy styled-components that haven't been migrated yet.
 *
 * **Why this matters:**
 * - Chakra props (`zIndex="sticky"`) work with the theme system
 * - Styled-components can't access Chakra's CSS variables reliably
 * - Raw constants bypass the theme, making future changes harder
 *
 * **When you see `Z_INDEX` usage, consider refactoring to Chakra components.**
 *
 * ## Token Hierarchy
 *
 * Tokens are ordered from lowest to highest z-index value.
 * Values are derived from Chakra's defaults (BASE) for consistency.
 */

// Chakra UI default z-index values (our source of truth)
const BASE = {
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
} as const;

// Raw z-index values for use in styled-components and CSS-in-JS
export const Z_INDEX = {
  // Low-level elements
  seatingMap: BASE.docked,
  spinner: BASE.docked + 5,

  // Dropdowns and overlays
  customOverlay: BASE.dropdown,
  dropdown: BASE.dropdown,

  // Sticky elements and navbar hierarchy
  stickyBelowAlt: BASE.sticky - 3,
  stickyBelow: BASE.sticky - 2,
  navbarBanner: BASE.sticky - 2,
  navbarTabs: BASE.sticky - 1,
  sticky: BASE.sticky,

  // Banners and drawers
  banner: BASE.banner,
  drawer: BASE.overlay - 50,

  // Overlays and modals
  overlay: BASE.overlay,
  dashboardSubmenu: BASE.modal - 2,
  dashboardSidebar: BASE.modal - 1,
  modal: BASE.modal,
  // Popovers
  popover: BASE.popover,
  skipLink: BASE.skipLink,
  toast: BASE.toast,
  tooltip: BASE.tooltip,

  // Maximum
  max: 2147483646,
} as const;

// Chakra theme tokens (use in Chakra props: zIndex="sticky")
export const zIndexTokens = {
  seatingMap: { value: Z_INDEX.seatingMap },
  spinner: { value: Z_INDEX.spinner },
  customOverlay: { value: Z_INDEX.customOverlay },
  dropdown: { value: Z_INDEX.dropdown },
  stickyBelowAlt: { value: Z_INDEX.stickyBelowAlt },
  stickyBelow: { value: Z_INDEX.stickyBelow },
  navbarBanner: { value: Z_INDEX.navbarBanner },
  navbarTabs: { value: Z_INDEX.navbarTabs },
  sticky: { value: Z_INDEX.sticky },
  banner: { value: Z_INDEX.banner },
  drawer: { value: Z_INDEX.drawer },
  overlay: { value: Z_INDEX.overlay },
  dashboardSubmenu: { value: Z_INDEX.dashboardSubmenu },
  dashboardSidebar: { value: Z_INDEX.dashboardSidebar },
  modal: { value: Z_INDEX.modal },
  popover: { value: Z_INDEX.popover },
  skipLink: { value: Z_INDEX.skipLink },
  toast: { value: Z_INDEX.toast },
  tooltip: { value: Z_INDEX.tooltip },
  max: { value: Z_INDEX.max },
};
